import React from 'react';
import { StepBreadcrumbsProps } from '@helpers/Models';

/**
 * `OrderProcessingBreadcrumbs` is a functional component that renders a breadcrumb-style
 * progress indicator for order processing steps.
 *
 * Each step is displayed as a horizontal segment, and the current progress is indicated
 * through color changes for active and completed steps.
 *
 * ## Props:
 * @param {string[]} steps - An array of step names to be displayed in order.
 * @param {string} activeStep - The name of the current active step.
 * @param {string[]} finishedSteps - An array of step names that are marked as completed.
 *
 * ## Behavior:
 * - The active step is highlighted with a primary background color.
 * - Finished steps have a different background to indicate progress.
 * - Remaining steps are shown in a muted style.
 * - The first and last steps have rounded corners.
 * - Non-edge steps are skewed to create a chevron-style appearance.
 *
 * @returns {JSX.Element} A styled breadcrumb UI component for tracking order steps.
 *
 */
export const OrderProcessingBreadcrumbs: React.FC<StepBreadcrumbsProps> = ({
  steps,
  activeStep,
  finishedSteps,
}) => {
  return (
    <div className="flex w-full font-medium h-10">
      {steps.map((step, index) => {
        const isFirst = index === 0;
        const isLast = index === steps.length - 1;
        const isActive = step === activeStep;
        const isFinished = finishedSteps.includes(step);

        let bgColor = 'bg-white text-gray-400';
        if (isFinished) bgColor = 'bg-[#00483433] text-black'; // Finished color
        if (isActive) bgColor = 'bg-primary text-white'; // Active color
        return (
          <div
            key={step}
            className={`
              relative flex-1 h-full z-10 
              ${!isLast ? 'pr-2' : ''}
            `}
          >
            <div
              className={`
                h-full w-full px-4 flex items-center justify-center -skew-x-6
                ${bgColor}
                ${isFirst ? 'rounded-l-full' : ''}
                ${isLast ? 'rounded-r-full border border-[#CFD6DC]' : ''}
              `}
            >
              <span className="truncate">{step}</span>
            </div>

            {/* Diagonal Separator */}
            {!isLast && (
              <div className="absolute top-0 right-0 w-2 h-full bg-white transform -skew-x-6 origin-top-left" />
            )}
          </div>
        );
      })}
    </div>
  );
};
